import { useAtomValue } from 'jotai'
import { useAuthenticatedUser } from '~/auth/user.hooks'
import { currentScopeState } from '~/state'
import { staffAccessState } from './useNodeStaffAccess'
import { useScope } from './useScope'

const SPARK_FEATURE = 'pbstckBidder'

/**
 * Whether the current scope has been onboarded on Spark (`pbstck-bidder`), i.e. the Spark team has
 * provisioned it: the customer's `Spark` seat, the `*-spark` internal seats and the scope-level stack.
 *
 * Deliberately `onboardedFeatures` rather than `hasFeature('pbstckBidder')`: this is the provisioning
 * predicate, the seat only exists once the onboarding toggle has been flipped. It says nothing about who
 * may see Spark. For that, a viewer-facing surface asks {@link useSparkStaffAccess}.
 */
export const useIsSparkOnboarded = (): boolean => {
  const currentScope = useAtomValue(currentScopeState)
  return currentScope.onboardedFeatures?.includes(SPARK_FEATURE) ?? false
}

type SparkScope = Parameters<typeof staffAccessState>[0]
type SparkViewer = Parameters<typeof staffAccessState>[1]

/** Spark (`pbstckBidder`) for the person looking, from the scope's two toggles. See {@link staffAccessState}. */
export const sparkStaffState = (scope: SparkScope, user: SparkViewer) => {
  const { hasAccess, has, isStaffOnly, isStaffOnProduct, isLockedToStaff } = staffAccessState(scope, user, SPARK_FEATURE)
  const isOnboarded = scope.onboardedFeatures?.includes(SPARK_FEATURE) ?? false
  return {
    hasSparkAccess: hasAccess,
    hasSpark: has,
    isStaffOnly,
    isStaffOnSpark: isStaffOnProduct,
    isLockedToStaff,
    /** Onboarded and usable by this viewer: what a viewer-facing surface passes as `isSparkOnboarded`. */
    viewerOnboarded: isOnboarded && has,
  }
}

/**
 * Spark for the person looking. Onboarded with user access off is the staff-only state: Pubstack admins
 * use Spark as on any scope that has it (with a shield), everybody else finds it not available.
 */
export const useSparkStaffAccess = () => sparkStaffState(useScope(), useAuthenticatedUser())
